/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useParams } from 'react-router-dom';
import useFetchList from 'hooks/useFetchList';
import {
  ASSESSMENT_STATUSES,
  GET_ASSESSMENTS_COLUMNS,
} from 'components/Assessments/constants';
import { useSelector } from 'react-redux';
import ConfirmResendingInvitation from './widgets/ConfirmResendingInvitation';
import { NULL_QUERY_DELAY } from '../../Shared/constants';
import EnableClaimModal from '../../Claims/widgets/EnableClaimModal';
import ShowAssessmentLinkModal from './widgets/ShowAssessmentLinkModal';
import AssessmentsTable from './widgets/AssessmentsTable';
import { convertDataFromApi, useGenerateColumns } from '../../Shared/Table/helpers';
import { TABLE_KEYS } from '../../../redux_store/reducer/reducers/tableReducer/tableReducer';

const defaultConfirmResendInvitationState = {
  patientUUID: '',
  survey: '',
  accountUUID: '',
};

const AssessmentsList = forwardRef(({
  setFilterStyle,
  setTotalCount,
  tableTitle,
  q,
  hideMenuActions,
  columnList,
}, ref) => {
  const { en: { forms } } = useSelector((state) => state.i18n);
  const {
    metadata: { currentAccount },
    isDistributionSponsor,
  } = useSelector((state) => state.user);
  const { accountUuid: paramAccountUuid } = useParams();
  const [prevTotalCount, setPrevTotalCount] = useState();
  const [modifiedAssessments, setModifiedAssessments] = useState();
  const [enableClaimModalInfo, setEnableClaimModalInfo] = useState(null);
  const [assessmentLinkForShareModal, setAssessmentLinkForShareModal] = useState(null);
  const [openConfirmResentDialog, toggleConfirmResentDialog] = useState(defaultConfirmResendInvitationState);
  const baseEndpoint = `/accounts/${paramAccountUuid || currentAccount.uuid}`;
  const tableKey = isDistributionSponsor ? TABLE_KEYS.assessmentsDSTable : TABLE_KEYS.assessmentsTable;
  const columns = useGenerateColumns(columnList || GET_ASSESSMENTS_COLUMNS, tableKey, {}, {}).columns;
  const fields = columns.map((columnData) => columnData.accessorKey).join();

  const {
    items,
    totalAccountsCount,
    totalCount,
    completionRate,
    noShowRate,
    performanceRate,
    reloadData,
    clearFilter,
    loading: dataLoading,
    onLoadMoreRows,
    sort,
    onSort,
    filter,
    onFilter,
  } = useFetchList({
    baseEndpoint: baseEndpoint + '/surveys',
    baseParams: {
      fields: fields,
      q: q || undefined,
    },
    tableKey,
    canLoad: fields.length,
  });

  useEffect(() => {
    setTotalCount && setTotalCount(totalCount);
  }, [totalCount]);

  useEffect(() => {
    if (prevTotalCount !== totalCount) {
      setFilterStyle(!!filter?.reduce((accumulator, { value }) => {
        return accumulator + value.toString().replaceAll(' ', '').length;
      }, 0));
      setPrevTotalCount(totalCount);
    }
  }, [filter, totalCount]);

  useEffect(() => {
    if (items) {
      const newModifiedItems = convertDataFromApi(items, tableKey, filter, {
        claimable: (assessment) => assessment.claimExists === false,
        canRevertNoShowStatus: (assessment) => assessment.status === ASSESSMENT_STATUSES.noShow,
      });
      setModifiedAssessments(newModifiedItems);
    }
  }, [items]);

  const updateClaimableByUuid = (resourceUuid) => {
    const newModifiedItems = modifiedAssessments.map((item) => {
      return item.uuid === resourceUuid ? { ...item, claimable: false } : item;
    });
    setModifiedAssessments(newModifiedItems);
  };

  const reloadAssessmentsList = (hasFilter) => {
    if (!hasFilter) {
      clearFilter();
    } else {
      reloadData({ delay: NULL_QUERY_DELAY });
    }
  };

  useImperativeHandle(ref, () => ({
    reloadList(v) {
      reloadAssessmentsList(v);
    },
  }));

  const _aggregations = useMemo(
    () => {
      if (dataLoading && !modifiedAssessments) {
        return null;
      }

      const res = {};
      if (totalAccountsCount || totalAccountsCount === 0) {
        res.totalAccountsCount = totalAccountsCount;
      }
      if (completionRate || completionRate === 0) {
        res.completionRate = completionRate;
      }
      if (noShowRate || noShowRate === 0) {
        res.noShowRate = noShowRate;
      }
      if (performanceRate || performanceRate === 0) {
        res.performanceRate = performanceRate;
      }

      return res;
    },
    [dataLoading],
  );

  return (
    <>
      {enableClaimModalInfo && (
        <EnableClaimModal
          open
          handleClose={() => setEnableClaimModalInfo(null)}
          codeType='assessment'
          accountUuid={enableClaimModalInfo.accountUUID}
          uuid={enableClaimModalInfo.uuid}
          updateClaimableByUuid={updateClaimableByUuid}
        />
      )}
      {openConfirmResentDialog.survey && (
        <ConfirmResendingInvitation
          source='survey'
          handleClose={() =>
            toggleConfirmResentDialog(defaultConfirmResendInvitationState)
          }
          reloadData={reloadData}
          resendInvitationData={openConfirmResentDialog}
          subtitle={forms.invitationWillBeSentSubtitle}
        />
      )}
      <ShowAssessmentLinkModal
        open={!!assessmentLinkForShareModal}
        handleClose={() => setAssessmentLinkForShareModal(null)}
        assessmentLink={assessmentLinkForShareModal}
      />
      <AssessmentsTable
        modifiedAssessments={modifiedAssessments}
        dataLoading={dataLoading}
        toggleConfirmResentDialog={toggleConfirmResentDialog}
        reloadData={reloadData}
        totalCount={totalCount}
        totalAccountsCount={totalAccountsCount}
        setEnableClaimModalInfo={setEnableClaimModalInfo}
        setAssessmentLinkForShareModal={setAssessmentLinkForShareModal}
        onLoadMoreRows={onLoadMoreRows}
        tableKey={tableKey}
        sort={sort}
        onSort={onSort}
        filter={filter}
        tableTitle={tableTitle}
        onFilter={onFilter}
        columns={columns}
        reloadAssessmentsList={reloadAssessmentsList}
        hideMenuActions={hideMenuActions}
        aggregations={_aggregations}
      />
    </>
  );
});

export default AssessmentsList;
